import React, { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { BylineConnectionIconStack } from '../BylineConnectionIconStack.tsx';
import {
	type BylineConnectionIcon,
	MAX_BYLINE_CONNECTION_ICONS,
} from '../utils/getBylineConnectionIcons.ts';

import { useOutboundBylineSsrState } from './useOutboundBylineSsrState.ts';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
	teamworkGraph: {
		id: 'byline-connections.teamwork-graph-label',
		defaultMessage: 'Teamwork Graph',
		description: 'Label for the Teamwork Graph byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		whiteSpace: 'normal',
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
	},
});

const placeholderIcons: BylineConnectionIcon[] = Array.from(
	{ length: MAX_BYLINE_CONNECTION_ICONS },
	(_, index) => ({
		key: `placeholder-link-${index}`,
		label: '',
		icon: {
			kind: 'element',
			key: `placeholder-link-${index}`,
			label: '',
			node: (
				<LinkIcon
					label=""
					color={token('color.icon.disabled')}
					testId="byline-connections-ssr-placeholder-icon"
				/>
			),
		},
	}),
);

type BylineConnectionsPlaceholderProps = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	fallback?: ReactNode;
};

export const BylineConnectionsPlaceholder = ({
	contentId,
	connectionAncestors,
	fallback = null,
}: BylineConnectionsPlaceholderProps): React.JSX.Element | null => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { formatMessage } = useIntl();
	const outboundState = useOutboundBylineSsrState(renderServerPlaceholder ? contentId : undefined);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const shouldRenderPlaceholder =
		outboundState?.status === 'present' ||
		outboundState?.status === 'unavailable' ||
		devDocsConnection !== null;

	if (!renderServerPlaceholder) {
		return <>{fallback}</>;
	}
	if (!shouldRenderPlaceholder) {
		return null;
	}

	return (
		<span
			css={styles.wrapper}
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid="byline-connections-ssr-placeholder"
		>
			<span css={styles.trigger} aria-hidden="true">
				<BylineConnectionIconStack
					links={[]}
					additionalIcons={placeholderIcons}
					appearance="placeholder"
					reserveMaximumWidth
				/>
				{formatMessage(i18n.teamworkGraph)}
			</span>
		</span>
	);
};
